import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from '@tanstack/react-router';

import { useMedia } from '@/_containers';
import { useGetRevenueLockdowns } from '@/_pages/settings/revenue-lockdowns/shared';
import { useFinancialOverridePermission } from '@/_providers';
import { currencyInput } from '@/_schemas/generic';
import type { OnPartialUpdate, OnUpdate } from '@/_schemas/types';
import {
  dateTime,
  defaultApiParamStr,
  queryClient,
  resetFormState,
  snackbar,
  useBoolean,
  useEdit,
  useInvalidate,
  usePostAuditLog,
  useProfileCrudActions,
} from '@/_shared';

import { useAgreementCommissions } from './use-commissions';
import { useDeliverablePage } from './use-deliverable-page';
import { useRevisionHistory } from './use-revision-history';
import { useSubsidies } from './use-subsidies';

import { useDeleteMutation, useGetAgreement, useUpdateMutation } from '../apis';
import { agreementFormToUpdate, agreementQueryToForm } from '../converters';
import { useDeliverableBulkUpdateMutation } from '../deliverables/api';
import { deliverableFormToQuery, validatePriceQty } from '../deliverables/helper';
import { deliverableDiffOnlyUsesAllowlistedKeys } from '../deliverables/revenue-lockdown-editable-deliverable-keys';
import { useFormDefinition } from '../form-context';
import {
  getLockedAgreementExecutionFieldKeys,
  getLockedAgreementExecutionFieldLabels,
} from '../lockdown-submit-validation';
import {
  getRevenueLockdownIdsForDateChange,
  REVENUE_LOCKDOWN_BLOCKED_MESSAGE,
  REVENUE_LOCKDOWN_CONFLICT_MESSAGE,
  REVENUE_LOCKDOWN_NO_OVERRIDE_PERMISSION_MESSAGE,
} from '../revenue-lockdown-guard';
import type { AgreementFormSchema, AgreementQuerySchema, AgreementUpdateSchema } from '../schemas';

type LockdownOverrideArgs = { pin: string; justification: string };

export function usePage() {
  const { id } = useParams({ strict: false });
  usePostAuditLog({ endpoint: 'agreements', id: `${id}` });

  const nav = useNavigate();
  const edit = useEdit();
  const canOverrideLockdown = useFinancialOverridePermission();

  const form = useFormDefinition();

  const { setHasToInvalidate, removeQuery } = useInvalidate(['AGREEMENT', id ?? '']);
  const agreement = useGetAgreement(`${id}`);
  const autoCommissionRequired = useBoolean(true);

  const { manageCommissions, isManageCommissionPending } = useAgreementCommissions(
    form,
    agreement?.data?.data?.agreement_commissions || []
  );

  const { manageSubsidies, isManageSubsidiesPending } = useSubsidies(form, agreement?.data?.data?.subsidies || []);

  const deleteMutation = useDeleteMutation();
  const updateMutation = useUpdateMutation(`${id}`);
  const isUpdatePending = updateMutation.isPending;
  const [currentAgreementDate, parent_id, execution_date] = form.watch([
    'agreement_date',
    'parent_id',
    'execution_date',
  ]);
  const isAddendum = !!parent_id;
  //!important 500 per_page
  const { data: lockdownRes } = useGetRevenueLockdowns(defaultApiParamStr);
  const lockdowns = useMemo(() => lockdownRes?.data ?? [], [lockdownRes?.data]);

  const [lockdownModalOpen, setLockdownModalOpen] = useBoolean(false);
  const [lockdownModalFields, setLockdownModalFields] = useState<string[]>([]);
  const currentAgreementRef = useRef<AgreementFormSchema | null>(null);
  const pendingSubmitRef = useRef<AgreementFormSchema | null>(null);
  const pendingOverrideActionRef = useRef<((args: LockdownOverrideArgs) => void | Promise<void>) | null>(null);

  const isExecutionDateLocked = useMemo(() => {
    if (!execution_date) return false;
    return (
      getRevenueLockdownIdsForDateChange({
        lockdowns,
        oldStart: execution_date,
        oldEnd: execution_date,
        newStart: execution_date,
        newEnd: execution_date,
      }).length > 0
    );
  }, [execution_date, lockdowns]);

  const {
    isSuccess: dIsSuccess,
    isPending: dIsPending,
    mutateAsync: deliverablesMutateUpdateAsync,
  } = useDeliverableBulkUpdateMutation();

  const manageDeliverables = useCallback(
    async (data: AgreementFormSchema, override?: LockdownOverrideArgs) => {
      if (!form.formState.dirtyFields.tempAgreement_deliverables) return;

      const { tempAgreement_deliverables } = data;
      for (let i = 0; i < tempAgreement_deliverables.length; i++) {
        const { price, quantity } = tempAgreement_deliverables[i];
        const res = validatePriceQty(price, currencyInput.formToUpdateConverter(quantity) ?? 0, isAddendum);
        if (res !== null) {
          snackbar.error('Please check deliverables. ' + res.msg);
          return;
        }
      }

      const nDeliverables = deliverableFormToQuery(tempAgreement_deliverables).map((d) => ({
        ...d,
      }));
      form.setValue('agreement_deliverables', tempAgreement_deliverables);

      if (nDeliverables.length > 0) {
        const res = await deliverablesMutateUpdateAsync({
          items: nDeliverables,
          lockdown_override_pin: override?.pin,
          override_justification_note: override?.justification,
        });
        if (res) {
          queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY'] });
        }
      }
    },
    [deliverablesMutateUpdateAsync, form, isAddendum]
  );

  const handleSuccessSideEffects = useCallback(() => {
    removeQuery(['AGREEMENTS']);
    setHasToInvalidate.setTrue();
  }, [removeQuery, setHasToInvalidate]);

  const {
    onUpdate: onUpdateCrud,
    onPartialUpdate: onPartialUpdateCrud,
    onDelete: onDeleteCrud,
    isDeleting,
    isPending: isCrudPending,
    isSuccess,
  } = useProfileCrudActions<AgreementFormSchema, AgreementUpdateSchema, { data: AgreementQuerySchema }>({
    form,
    edit,
    formToMutation: agreementFormToUpdate,
    updateMutation,
    deleteMutation,
    deleteVariables: { id: `${id}` },
    updateSuccessMessage: `${isAddendum ? 'Addendum' : 'Agreement'} has been updated.`,
    deleteSuccessMessage: `${isAddendum ? 'Addendum' : 'Agreement'} has been deleted.`,
    onSuccessSideEffects: handleSuccessSideEffects,
    onDeleteSuccess: () => {
      nav({ to: '/agreements' });
    },
  });

  const onUpdate: OnUpdate<AgreementUpdateSchema, { data: AgreementQuerySchema }> = useCallback(
    async (
      v,
      msg = `${isAddendum ? 'Addendum' : 'Agreement'} has been updated.`,
      cancelEdit = true,
      shouldResetForm = true
    ) => {
      return onUpdateCrud(v, msg, cancelEdit, shouldResetForm);
    },
    [isAddendum, onUpdateCrud]
  );

  const onPartialUpdate: OnPartialUpdate<AgreementUpdateSchema> = useCallback(
    async (partialValues, msg, cancelEdit, shouldResetForm) => {
      const res = await onPartialUpdateCrud(partialValues, msg, cancelEdit, shouldResetForm);

      if (res) {
        currentAgreementRef.current = form.getValues();
      }

      if (partialValues.activate_subsidy_billing === true && msg === 'Subsidies billing status updated') {
        edit[1].setTrue();
      }

      return res;
    },
    [edit, form, onPartialUpdateCrud]
  );

  const requestDeliverablesExecutionLockOverride = useCallback(
    (opts: { affectedFields?: string[]; onConfirm: (args: LockdownOverrideArgs) => void | Promise<void> }) => {
      if (!canOverrideLockdown) {
        snackbar.error(REVENUE_LOCKDOWN_NO_OVERRIDE_PERMISSION_MESSAGE);
        return;
      }

      pendingSubmitRef.current = null;
      pendingOverrideActionRef.current = opts.onConfirm;
      setLockdownModalFields(opts.affectedFields ?? ['Agreement Execution Date', 'Deliverables']);
      setLockdownModalOpen.setTrue();
    },
    [canOverrideLockdown, setLockdownModalOpen]
  );

  const onSubmit = useCallback(
    async (data: AgreementFormSchema) => {
      const lockedFieldKeys = getLockedAgreementExecutionFieldKeys({
        data,
        currentData: currentAgreementRef.current,
        dirtyFields: form.formState.dirtyFields,
        lockdowns,
      });

      if (lockedFieldKeys.length > 0) {
        if (!canOverrideLockdown) {
          for (const key of lockedFieldKeys) {
            form.setError(key, { type: 'manual', message: REVENUE_LOCKDOWN_CONFLICT_MESSAGE });
          }
          return;
        }

        const lockedFieldLabels = getLockedAgreementExecutionFieldLabels({
          data,
          currentData: currentAgreementRef.current,
          dirtyFields: form.formState.dirtyFields,
          lockdowns,
        });
        pendingSubmitRef.current = data;
        setLockdownModalFields(lockedFieldLabels);
        setLockdownModalOpen.setTrue();
        return;
      }

      const dirtyTempDeliverables = form.formState.dirtyFields.tempAgreement_deliverables;
      const hasDisallowedDeliverableChange =
        dirtyTempDeliverables &&
        data.tempAgreement_deliverables.some((temp) => {
          const orig = data.agreement_deliverables.find((o) => o.id === temp.id);
          return !deliverableDiffOnlyUsesAllowlistedKeys(orig ?? null, temp);
        });
      if (hasDisallowedDeliverableChange && isExecutionDateLocked) {
        requestDeliverablesExecutionLockOverride({
          onConfirm: async ({ pin, justification }) => {
            await manageDeliverables(data, { pin, justification });
            await manageCommissions();
            await manageSubsidies();
            onPartialUpdate({
              ...agreementFormToUpdate(data),
              lockdown_override_pin: pin,
              override_justification_note: justification,
            });
          },
        });
        return;
      }

      await manageDeliverables(data);

      await manageCommissions();
      await manageSubsidies();

      onPartialUpdate(agreementFormToUpdate(data));
    },
    [
      lockdowns,
      form,
      onPartialUpdate,
      manageCommissions,
      manageSubsidies,
      manageDeliverables,
      setLockdownModalOpen,
      canOverrideLockdown,
      isExecutionDateLocked,
      requestDeliverablesExecutionLockOverride,
    ]
  );

  const lockdownModal = useMemo(() => {
    return {
      open: lockdownModalOpen,
      message: REVENUE_LOCKDOWN_BLOCKED_MESSAGE,
      affectedFields: lockdownModalFields,
      canOverride: canOverrideLockdown,
      onCancel: () => {
        pendingSubmitRef.current = null;
        pendingOverrideActionRef.current = null;
        setLockdownModalOpen.setFalse();
      },
      onOverride: () => {
        // UI handles stage switching; nothing needed here for now
      },
      onConfirmOverride: async ({ pin, justification }: LockdownOverrideArgs) => {
        if (!canOverrideLockdown) {
          snackbar.error(REVENUE_LOCKDOWN_NO_OVERRIDE_PERMISSION_MESSAGE);
          return;
        }

        const pendingOverrideAction = pendingOverrideActionRef.current;
        if (pendingOverrideAction) {
          pendingOverrideActionRef.current = null;
          setLockdownModalOpen.setFalse();
          await pendingOverrideAction({ pin, justification });
          return;
        }

        const pending = pendingSubmitRef.current;
        if (!pending) {
          return;
        }

        pendingSubmitRef.current = null;
        setLockdownModalOpen.setFalse();
        await manageDeliverables(pending, { pin, justification });
        await manageCommissions();
        await manageSubsidies();
        onPartialUpdate({
          ...agreementFormToUpdate(pending),
          lockdown_override_pin: pin,
          override_justification_note: justification,
        });
      },
      isSubmitting: isUpdatePending,
    };
  }, [
    canOverrideLockdown,
    isUpdatePending,
    lockdownModalFields,
    lockdownModalOpen,
    manageDeliverables,
    manageCommissions,
    manageSubsidies,
    onPartialUpdate,
    setLockdownModalOpen,
  ]);

  const requestRevenueLockdownOverride = useCallback(
    (opts: { affectedFields: string[]; onConfirm: (args: LockdownOverrideArgs) => void | Promise<void> }) => {
      if (!canOverrideLockdown) {
        snackbar.error(REVENUE_LOCKDOWN_NO_OVERRIDE_PERMISSION_MESSAGE);
        return;
      }

      pendingSubmitRef.current = null;
      pendingOverrideActionRef.current = opts.onConfirm;
      setLockdownModalFields(opts.affectedFields);
      setLockdownModalOpen.setTrue();
    },
    [canOverrideLockdown, setLockdownModalOpen]
  );

  const onDelete = onDeleteCrud;

  const revisionHistory = useRevisionHistory(form, onUpdate);
  const deliverablePage = useDeliverablePage(form, edit[0], {
    isExecutionDateLocked,
    canOverrideLockdown,
    requestRevenueLockdownOverride,
  });

  useEffect(() => {
    const { data: response } = agreement;
    if (response) {
      const formValues = agreementQueryToForm(response.data);
      currentAgreementRef.current = formValues;
      resetFormState(form, formValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement.data]);

  useEffect(() => {
    if (currentAgreementDate && edit[0]) {
      form.setValue(
        'expiry_date',
        dateTime.format(dateTime.add(currentAgreementDate, 90, 'days').startOf('day'), 'YYYY-MM-DD')
      );
    }
    // !Important to have this check to avoid unnecessary updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAgreementDate]);

  const { onImageUpdate } = useMedia(onUpdate);

  console.log(form.formState.errors);

  return {
    id,
    form,
    edit,
    isPending: isCrudPending || dIsPending || isManageCommissionPending || isManageSubsidiesPending,
    isSuccess: isSuccess || dIsSuccess,
    isDeleting,
    onSubmit,
    onDelete,
    onPartialUpdate,
    onImageUpdate,
    revisionHistory,
    isReverting: agreement.isFetching || isDeleting || isUpdatePending,

    deliverablePage,
    autoCommissionRequired,

    revenueLockdowns: lockdowns,
    canOverrideLockdown,
    lockdownModal,
    requestRevenueLockdownOverride,
  };
}

export type UsePageType = ReturnType<typeof usePage>;
